<template>
  <tr :class="{ 'linha-quebrada': quebrada }">
    <!-- View -->
    <td>
      <span class="view-nome">{{ model.view_name }}</span>
    </td>

    <!-- Descrição -->
    <td class="col-descricao">
      <span v-if="temDescricao" class="view-descricao">{{ model.descricao }}</span>
      <span v-else class="selo-sem-descricao" title="Esta view não possui descrição">
        <fa-icon icon="exclamation-triangle" /> Sem descrição
      </span>
    </td>

    <!-- Ativo -->
    <td>
      <span class="pilula" :class="ativo ? 'pilula--sim' : 'pilula--nao'">
        {{ ativo ? 'Sim' : 'Não' }}
      </span>
    </td>

    <!-- Carga vinculada -->
    <td class="col-carga">
      <router-link
        v-if="temCarga"
        :to="`/configuracoes/cargas?q=${model.view_name}`"
        class="chip-carga"
        :class="chipCargaClasse"
        :title="tituloCarga"
      >
        <fa-icon :icon="iconeCarga" class="chip-carga__icon" />
        <span class="chip-carga__label">Ver carga</span>
        <span v-if="tempoCarga" class="chip-carga__tempo">{{ tempoCarga }}</span>
      </router-link>
      <span v-else class="sem-carga">sem carga vinculada</span>
    </td>

    <!-- Ações -->
    <td class="actions text-right">
      <CButton color="info" size="sm" title="Visualizar SQL" @click="$emit('visualizar', model)">
        <fa-icon icon="eye" />
      </CButton>
      <CButton color="primary" size="sm" title="Editar SQL" @click="$emit('editar', model)">
        <fa-icon icon="pencil-alt" />
      </CButton>
      <LogAlteracaoDados
        tabela="sql_erp"
        :chave="model.view_name"
        :registro-label="model.view_name"
        :restorable="true"
        @restaurar="(sql) => $emit('restaurar', { model, sql })"
      >
        <template #trigger="{ open }">
          <CButton
            color="secondary"
            variant="outline"
            size="sm"
            class="btn-acao-log"
            title="Log de alterações"
            @click="open"
          >
            <fa-icon icon="clipboard-list" />
          </CButton>
        </template>
      </LogAlteracaoDados>
      <CButton color="danger" size="sm" title="Excluir" @click="$emit('excluir', model)">
        <fa-icon icon="trash" />
      </CButton>
    </td>
  </tr>
</template>

<script>
import LogAlteracaoDados from '@/components/Log/LogAlteracaoDados';
import { cargaDe, cargaQuebrada } from './sqlCargaSaude';
import { jaExecutou, relTime } from '@/helpers/cargasSaude';

export default {
  name: 'ViewRow',
  components: { LogAlteracaoDados },
  props: {
    model: { type: Object, required: true },
  },
  computed: {
    ativo() {
      return +this.model.ativo === 1;
    },
    temDescricao() {
      const desc = (this.model.descricao || '').trim();
      return desc !== '' && desc !== this.model.view_name;
    },
    carga() {
      // `_carga` vem pré-normalizada da listagem; o fallback cobre usos avulsos.
      return this.model._carga !== undefined ? this.model._carga : cargaDe(this.model);
    },
    temCarga() {
      return !!this.carga;
    },
    quebrada() {
      return cargaQuebrada(this.model, this.carga);
    },
    ultima() {
      return this.carga && this.carga.ultimaSincronizacao;
    },
    jaExecutou() {
      return jaExecutou(this.ultima);
    },
    iconeCarga() {
      if (!this.jaExecutou) {
        return 'clock';
      }
      return this.ultima.sucesso ? 'check' : 'times';
    },
    chipCargaClasse() {
      if (!this.jaExecutou) {
        return 'chip-carga--nunca';
      }
      return this.ultima.sucesso ? 'chip-carga--ok' : 'chip-carga--falha';
    },
    tempoCarga() {
      if (!this.jaExecutou) {
        return '';
      }
      return relTime(this.ultima.fim || this.ultima.inicio);
    },
    tituloCarga() {
      if (!this.jaExecutou) {
        return 'Carga vinculada, mas nunca sincronizada';
      }
      if (!this.ultima.sucesso) {
        return this.ultima.msg || 'A última sincronização falhou';
      }
      return 'Última sincronização concluída com sucesso';
    },
  },
};
</script>

<style scoped>
.view-nome {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 600;
  color: #3a3e81;
}

.col-descricao {
  max-width: 420px;
}

.view-descricao {
  color: #5a6072;
}

.selo-sem-descricao {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
  color: #9a6a12;
  background: #fdf3e2;
  border: 1px solid #f3e0bd;
  border-radius: 4px;
}

.selo-sem-descricao svg {
  color: #c1751f;
}

/* Pílula Ativo */
.pilula {
  display: inline-block;
  padding: 2px 12px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
}

.pilula--sim {
  color: #2a8a52;
  background: #e7f6ec;
}

.pilula--nao {
  color: #c0392b;
  background: #fdeaea;
}

/* Chip "Ver carga" */
.chip-carga {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  text-decoration: none;
  white-space: nowrap;
}

.chip-carga:hover {
  text-decoration: none;
  filter: brightness(0.97);
}

.chip-carga__tempo {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.85;
}

.chip-carga--ok {
  color: #2a8a52;
  background: #e7f6ec;
  border: 1px solid #bfe3cc;
}

.chip-carga--falha {
  color: #c0392b;
  background: #fdeaea;
  border: 1px solid #f3cdc8;
}

.chip-carga--nunca {
  color: #9a6a12;
  background: #fdf3e2;
  border: 1px solid #f3e0bd;
}

.sem-carga {
  font-style: italic;
  color: #949aa8;
}

.linha-quebrada {
  background: #fffafa;
}

/* Ações */
.actions {
  white-space: nowrap;
}

.text-right {
  text-align: right;
}

.actions ::v-deep .btn {
  margin-left: 5px;
  border-radius: 6px;
}

.actions ::v-deep .btn-acao-log {
  color: #5a6072;
  background: #fff;
  border-color: #d3d8e0;
}

.actions ::v-deep .btn-acao-log:hover {
  color: #3a3e81;
  background: #f6f7f9;
  border-color: #b9c0cc;
}
</style>
